/**
 * Staff Performance Component
 * Displays weighted performance scores for staff members
 * FOCUS: Staff can work across multiple departments with separate performance tracking
 */

import React, { useState, useMemo } from 'react';
import useStore from '../store/useStore';
import './StaffPerformance.css';

const StaffPerformance = () => {
  const { staffPerformance, entities } = useStore();
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Get department from entity
  const getEntityDepartment = (entityTitle) => {
    const entity = entities?.find(e => e.Title === entityTitle);
    
    if (!entity) return 'General';
    
    // Option 1: Extract from Description field
    if (entity.Description) {
      const deptMatch = entity.Description;
      if (deptMatch) return deptMatch;
    }
    
    // Option 2: Use Category if available
    if (entity.Category) {
      return entity.Category;
    }
    
    // Option 3: Use EntityType as fallback grouping
    if (entity.EntityType) {
      return `${entity.EntityType} Team`;
    }
    
    return 'General';
  };

  // Group by department, calculate staff performance PER DEPARTMENT
  const departmentPerformance = useMemo(() => {
    if (!staffPerformance || staffPerformance.length === 0) {
      return null;
    }

    const departments = {};

    // Process each staff member
    staffPerformance.forEach(staff => {
      // Group this staff's assignments by department
      const assignmentsByDept = {};

      staff.assignments.forEach(assignment => {
        const dept = getEntityDepartment(assignment.entityTitle);
        
        if (!assignmentsByDept[dept]) {
          assignmentsByDept[dept] = [];
        }
        
        assignmentsByDept[dept].push(assignment);
      });

      // Create staff entry for each department they work in
      Object.keys(assignmentsByDept).forEach(dept => {
        if (!departments[dept]) {
          departments[dept] = {
            name: dept,
            staffPerformances: [],
            totalWeight: 0,
            totalWeightedScore: 0,
            staffCount: 0,
          };
        }

        const deptAssignments = assignmentsByDept[dept];
        
        // Calculate staff performance within THIS department only
        const deptTotalWeight = deptAssignments.reduce((sum, a) => sum + a.weight, 0);
        const deptWeightedScore = deptAssignments.reduce((sum, a) => sum + a.weightedScore, 0);
        const deptPerformance = deptTotalWeight > 0 ? (deptWeightedScore / deptTotalWeight) * 100 : 0;
        
        const deptScoredCount = deptAssignments.filter(a => a.score !== null).length;
        const deptCompletionRate = deptAssignments.length > 0 
          ? (deptScoredCount / deptAssignments.length) * 100 
          : 0;

        // Determine status
        let status = 'Needs Improvement';
        if (deptPerformance >= 90) status = 'Excellent';
        else if (deptPerformance >= 75) status = 'Good';
        else if (deptPerformance >= 50) status = 'Fair';

        departments[dept].staffPerformances.push({
          ownerName: staff.ownerName,
          assignmentsCount: deptAssignments.length,
          scoredCount: deptScoredCount,
          unscoredCount: deptAssignments.length - deptScoredCount,
          completionRate: deptCompletionRate,
          totalWeight: deptTotalWeight,
          totalWeightedScore: deptWeightedScore,
          performanceScore: deptPerformance,
          overallStatus: status,
          assignments: deptAssignments,
        });

        departments[dept].totalWeight += deptTotalWeight;
        departments[dept].totalWeightedScore += deptWeightedScore;
      });
    });

    // Calculate department-level statistics
    Object.keys(departments).forEach(dept => {
      const deptData = departments[dept];
      
      // Count unique staff (in case we want to track this)
      const uniqueStaff = new Set(deptData.staffPerformances.map(s => s.ownerName));
      deptData.uniqueStaffCount = uniqueStaff.size;
      deptData.staffCount = deptData.staffPerformances.length; // Total entries (staff can be counted multiple times if cross-dept)
      
      // Department weighted average
      deptData.avgPerformance = deptData.totalWeight > 0 
        ? (deptData.totalWeightedScore / deptData.totalWeight) * 100 
        : 0;
      
      // Top performer in department
      deptData.topPerformer = [...deptData.staffPerformances].sort((a, b) => 
        b.performanceScore - a.performanceScore
      )[0];
      
      // Staff needing attention
      deptData.needsAttention = deptData.staffPerformances.filter(s => s.performanceScore < 50).length;
    });

    return departments;
  }, [staffPerformance, entities]);

  // Overall summary
  const overallSummary = useMemo(() => {
    if (!departmentPerformance) return null;

    const allDepts = Object.values(departmentPerformance);
    const totalWeight = allDepts.reduce((sum, d) => sum + d.totalWeight, 0);
    const totalWeightedScore = allDepts.reduce((sum, d) => sum + d.totalWeightedScore, 0);
    
    // Count unique staff across all departments
    const uniqueStaff = new Set();
    staffPerformance?.forEach(s => uniqueStaff.add(s.ownerName));
    
    const overallPerformance = totalWeight > 0 ? (totalWeightedScore / totalWeight) * 100 : 0;
    const topDept = [...allDepts].sort((a, b) => b.avgPerformance - a.avgPerformance)[0];
    
    return {
      departmentCount: allDepts.length,
      totalStaff: uniqueStaff.size,
      totalWeight,
      totalWeightedScore,
      overallPerformance,
      topDepartment: topDept,
    };
  }, [departmentPerformance, staffPerformance]);

  if (!staffPerformance || staffPerformance.length === 0) {
    return (
      <div className="staff-performance-empty">
        <div className="empty-icon">👥</div>
        <p>No staff assignments found</p>
        <p>Assign entities to staff members with weights to track performance</p>
        <div className="empty-help">
          <h4>How to set up staff performance tracking:</h4>
          <ol>
            <li>Add entities (Metrics, Projects, CSFs, etc.)</li>
            <li>Set an <strong>Owner</strong> for each entity</li>
            <li>Set a <strong>Weight</strong> (0-1) for importance</li>
            <li>Add <strong>Score signals</strong> (0-1) for performance</li>
            <li>Optional: Add "Dept: YourDept" in entity Description for grouping</li>
          </ol>
        </div>
      </div>
    );
  }

  const handleStaffClick = (staffKey) => {
    setSelectedStaff(selectedStaff === staffKey ? null : staffKey);
  };

  const toggleGroup = (groupName) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupName)) {
      newExpanded.delete(groupName);
    } else {
      newExpanded.add(groupName);
    }
    setExpandedGroups(newExpanded);
  };

  const getStatusClass = (score) => {
    if (score >= 90) return 'excellent';
    if (score >= 75) return 'good';
    if (score >= 50) return 'fair';
    return 'poor';
  };

  const getScoreColor = (score) => {
    if (score >= 90) return '#107C10';
    if (score >= 75) return '#0078D4';
    if (score >= 50) return '#FFB900';
    return '#E81123';
  };

  return (
    <div className="staff-performance">
      {/* Overall Summary */}
      {overallSummary && (
        <div className="performance-summary">
          <div className="summary-card primary">
            <div className="summary-icon">🏢</div>
            <div className="summary-content">
              <span className="summary-label">Overall Performance</span>
              <span 
                className={`summary-value large ${getStatusClass(overallSummary.overallPerformance)}`}
                style={{ color: getScoreColor(overallSummary.overallPerformance) }}
              >
                {overallSummary.overallPerformance.toFixed(1)}%
              </span>
              <span className="summary-sublabel">
                Across all departments
              </span>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon">🏷️</div>
            <div className="summary-content">
              <span className="summary-label">Departments</span>
              <span className="summary-value">{overallSummary.departmentCount}</span>
              <span className="summary-sublabel">{overallSummary.totalStaff} unique staff</span>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon">📊</div>
            <div className="summary-content">
              <span className="summary-label">Total Weighted Score</span>
              <span className="summary-value">
                {overallSummary.totalWeightedScore.toFixed(2)}
              </span>
              <span className="summary-sublabel">
                of {overallSummary.totalWeight.toFixed(2)} total weight
              </span>
            </div>
          </div>

          {overallSummary.topDepartment && (
            <div className="summary-card highlight">
              <div className="summary-icon">🏆</div>
              <div className="summary-content">
                <span className="summary-label">Top Department</span>
                <span className="summary-value">{overallSummary.topDepartment.name}</span>
                <span className="summary-detail">
                  {overallSummary.topDepartment.avgPerformance.toFixed(1)}% avg performance
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Department Performance Tables */}
      <div className="grouped-performance">
        {Object.values(departmentPerformance)
          .sort((a, b) => b.avgPerformance - a.avgPerformance)
          .map((dept, deptIndex) => (
          <div key={deptIndex} className="performance-group">
            {/* Department Header */}
            <div 
              className="group-header"
              onClick={() => toggleGroup(dept.name)}
            >
              <div className="group-header-left">
                <span className="group-expand-icon">
                  {expandedGroups.has(dept.name) ? '▼' : '▶'}
                </span>
                <h3>{dept.name}</h3>
                <span className="group-staff-count">
                  ({dept.uniqueStaffCount} staff, {dept.staffPerformances.length} assignment{dept.staffPerformances.length !== 1 ? 's' : ''})
                </span>
              </div>
              
              <div className="group-stats-inline">
                <div className="group-stat">
                  <span className="stat-label">Dept Avg:</span>
                  <span 
                    className={`stat-value ${getStatusClass(dept.avgPerformance)}`}
                    style={{ color: getScoreColor(dept.avgPerformance) }}
                  >
                    {dept.avgPerformance.toFixed(1)}%
                  </span>
                </div>
                <div className="group-stat">
                  <span className="stat-label">Total Weight:</span>
                  <span className="stat-value">{dept.totalWeight.toFixed(2)}</span>
                </div>
                <div className="group-stat">
                  <span className="stat-label">Weighted Score:</span>
                  <span className="stat-value">{dept.totalWeightedScore.toFixed(2)}</span>
                </div>
                {dept.needsAttention > 0 && (
                  <div className="group-stat alert">
                    <span className="stat-label">⚠️ Needs Attention:</span>
                    <span className="stat-value">{dept.needsAttention}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Department Content */}
            {expandedGroups.has(dept.name) && (
              <div className="group-content">
                <table className="performance-table">
                  <thead>
                    <tr>
                      <th>Staff Member</th>
                      <th>Assignments</th>
                      <th>Scored</th>
                      <th>Total Weight</th>
                      <th className="highlight-col">Weighted Score</th>
                      <th className="highlight-col">Dept Performance</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dept.staffPerformances
                      .sort((a, b) => b.performanceScore - a.performanceScore)
                      .map((staff, staffIndex) => {
                        const staffKey = `${dept.name}-${staff.ownerName}`;
                        return (
                      <React.Fragment key={staffIndex}>
                        <tr 
                          className={`staff-row ${selectedStaff === staffKey ? 'expanded' : ''}`}
                          onClick={() => handleStaffClick(staffKey)}
                        >
                          <td className="staff-name">
                            <span className="expand-icon">
                              {selectedStaff === staffKey ? '▼' : '▶'}
                            </span>
                            <strong>{staff.ownerName}</strong>
                          </td>
                          <td>{staff.assignmentsCount}</td>
                          <td>
                            <div className="scored-cell">
                              <span className="scored-count">{staff.scoredCount} / {staff.assignmentsCount}</span>
                              <div className="completion-bar">
                                <div 
                                  className="completion-fill" 
                                  style={{ width: `${staff.completionRate}%` }}
                                />
                              </div>
                              <span className="completion-rate">
                                {staff.completionRate.toFixed(0)}%
                              </span>
                            </div>
                          </td>
                          <td>
                            <span className="weight-badge">{staff.totalWeight.toFixed(2)}</span>
                          </td>
                          <td className="highlight-col weighted-score-col">
                            <span className="weighted-score-value">
                              {staff.totalWeightedScore.toFixed(2)}
                            </span>
                            <span className="weighted-score-label">
                              / {staff.totalWeight.toFixed(2)}
                            </span>
                          </td>
                          <td className="highlight-col performance-col">
                            <div className="performance-cell">
                              <span 
                                className={`performance-score ${getStatusClass(staff.performanceScore)}`}
                                style={{ color: getScoreColor(staff.performanceScore) }}
                              >
                                {staff.performanceScore.toFixed(1)}%
                              </span>
                              <div className="performance-bar">
                                <div 
                                  className={`performance-fill ${getStatusClass(staff.performanceScore)}`}
                                  style={{ 
                                    width: `${staff.performanceScore}%`,
                                    backgroundColor: getScoreColor(staff.performanceScore)
                                  }}
                                />
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={`status-badge ${getStatusClass(staff.performanceScore)}`}>
                              {staff.overallStatus}
                            </span>
                          </td>
                        </tr>

                        {/* Expanded Details */}
                        {selectedStaff === staffKey && (
                          <tr className="details-row">
                            <td colSpan="7">
                              <div className="assignment-details">
                                <h4>📋 {dept.name} Assignments for {staff.ownerName}</h4>
                                <div className="assignment-summary">
                                  <div className="summary-metric">
                                    <span className="metric-label">Dept Assignments:</span>
                                    <span className="metric-value">{staff.assignmentsCount}</span>
                                  </div>
                                  <div className="summary-metric">
                                    <span className="metric-label">Scored:</span>
                                    <span className="metric-value">{staff.scoredCount}</span>
                                  </div>
                                  <div className="summary-metric">
                                    <span className="metric-label">Unscored:</span>
                                    <span className="metric-value">{staff.unscoredCount}</span>
                                  </div>
                                  <div className="summary-metric highlight">
                                    <span className="metric-label">Dept Performance:</span>
                                    <span className="metric-value">
                                      {staff.performanceScore.toFixed(1)}%
                                    </span>
                                  </div>
                                </div>

                                <table className="assignments-table">
                                  <thead>
                                    <tr>
                                      <th>Entity</th>
                                      <th>Type</th>
                                      <th>Weight</th>
                                      <th>Score</th>
                                      <th className="highlight-col">Weighted Score</th>
                                      <th>Status</th>
                                      <th>Last Updated</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {staff.assignments
                                      .sort((a, b) => b.weight - a.weight)
                                      .map((assignment, idx) => (
                                      <tr 
                                        key={idx} 
                                        className={`assignment-row ${assignment.status.toLowerCase().replace(' ', '-')}`}
                                      >
                                        <td className="assignment-entity">
                                          <strong>{assignment.entityTitle}</strong>
                                        </td>
                                        <td>
                                          <span className="entity-type-badge">{assignment.entityType}</span>
                                        </td>
                                        <td>
                                          <span className="weight-badge">{assignment.weight.toFixed(2)}</span>
                                        </td>
                                        <td>
                                          {assignment.score !== null ? (
                                            <span className={`score-value ${getStatusClass(assignment.score * 100)}`}>
                                              {(assignment.score * 100).toFixed(0)}%
                                            </span>
                                          ) : (
                                            <span className="not-scored">Not scored</span>
                                          )}
                                        </td>
                                        <td className="highlight-col">
                                          <span className="weighted-score-badge">
                                            {assignment.weightedScore.toFixed(3)}
                                          </span>
                                        </td>
                                        <td>
                                          <span className={`status-badge ${assignment.status.toLowerCase().replace(' ', '-')}`}>
                                            {assignment.status}
                                          </span>
                                        </td>
                                        <td className="last-updated">
                                          {assignment.lastUpdated ? 
                                            new Date(assignment.lastUpdated).toLocaleDateString('en-US', {
                                              year: 'numeric',
                                              month: 'short',
                                              day: 'numeric'
                                            }) : 
                                            <span className="not-updated">Never</span>
                                          }
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )})}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default StaffPerformance;
